import { Component } from "../base/Component";
import { ComponentFactory } from "../../service/ComponentFactory";
import { EventUtil } from "../../util/EventUtil";
import { PageContext } from "../../service/PageContext";
import { moduletracking } from "../../Wyanoke/ModuleTracking";

interface ToastAlerts {
  offtrack: boolean;
  almostthere: boolean;
  deadline: boolean;
}

interface CMEGoals {
  UserGoal: {
    ContactId: string;
    Credits: string;
    StartDate: number;
    EndDate: number;
    Reminders: any[];
  };
  OnTrack: boolean;
  TotalCreditsEarned: number;
  GoalCreditsEarned: number;
  GoalCreditsRemaining: number;
  GoalCompletion: number;
  /** Tracks which CME goal toasts have been shown; defaults to false. Replaces cookie-based tracking. */
  toastalerts?: ToastAlerts;
  /** Backend may send PascalCase; we normalize to toastalerts so only one object exists. */
  ToastAlerts?: ToastAlerts;
}

interface UserProfile {
  cmegoals?: CMEGoals;
}

/**
 * CME Goal Notifications Component
 *
 * Manages CME goal reminder toasts that appear on News pages and Specialty Landing Pages.
 *
 * PURPOSE:
 * These reminders appear when a user is on Healio but outside of CME (e.g., in News).
 * They help users stay on track with their CME goals by providing timely reminders.
 *
 * REMINDER TYPES:
 * 1. Off Track Reminder - Shows at 30 days and 7 days before deadline when user is off track
 * 2. Almost There Reminder - Shows when user reaches percentage milestones (e.g., 75%)
 * 3. Deadline Is Here Reminder - Shows on the goal deadline day if goal is not complete
 *
 * KEY FEATURES:
 * - Each reminder type only appears ONCE per condition (enforced via cmegoals.toastalerts)
 * - Reminders are blocked for expired goals or goals that haven't started
 * - Toastalerts (offtrack, almostthere, deadline) prevent duplicate reminders
 * - Google Analytics event tracking for all opens and clicks
 *
 * See checkAndTriggerToasts() method for detailed business rules and logic.
 */
export class CMEGoalNotifications extends Component {
  // Event names for triggering toasters
  public static readonly ShowAlmostThereEvent = "CMEGoalNotifications:ShowAlmostThere";
  public static readonly ShowReachYourGoalEvent = "CMEGoalNotifications:ShowReachYourGoal";
  public static readonly DismissAlmostThereEvent = "CMEGoalNotifications:DismissAlmostThere";
  public static readonly DismissReachYourGoalEvent = "CMEGoalNotifications:DismissReachYourGoal";

  // Static registry to track component instances
  private static _instances: CMEGoalNotifications[] = [];

  /** Get first initialized instance (for debug / manual toast triggers). */
  public static getFirstInstance(): CMEGoalNotifications | null {
    return CMEGoalNotifications._instances.length > 0 ? CMEGoalNotifications._instances[0] : null;
  }

  private _creditGoalToast: HTMLElement | null = null;
  private _percentageGoalToast: HTMLElement | null = null;
  private _deadlineGoalToast: HTMLElement | null = null;
  private _scheduledPostLoginCheck: boolean = false;
  /** Default toastalerts when backend does not send them (e.g. before deploy). */
  private static readonly _defaultToastAlerts: ToastAlerts = {
    offtrack: false,
    almostthere: false,
    deadline: false
  };

  constructor(element: HTMLElement, pageContext: PageContext) {
    super(element, pageContext);
    // Register this instance
    CMEGoalNotifications._instances.push(this);
  }

  public async init(): Promise<void> {
    try {
      this.initCore();
    } catch (err) {
      console.warn("CMEGoalNotifications: init failed (non-fatal)", err);
    }
  }

  private initCore(): void {
    // Find the existing toast elements
    this._creditGoalToast = this._element.querySelector("#creditGoalToast");
    this._percentageGoalToast = this._element.querySelector("#percentageGoalToast");
    this._deadlineGoalToast = this._element.querySelector("#deadlineGoalToast");

    if (!this._creditGoalToast) {
      console.warn("CMEGoalNotifications: Credit goal toast not found");
    }

    if (!this._percentageGoalToast) {
      console.warn("CMEGoalNotifications: Percentage goal toast not found");
    }

    if (!this._deadlineGoalToast) {
      console.warn("CMEGoalNotifications: Deadline goal toast not found");
    }

    // Initially hide both toasts
    if (this._creditGoalToast) {
      this._creditGoalToast.classList.remove("show", "showing");
      this._creditGoalToast.style.display = "none";
    }

    if (this._percentageGoalToast) {
      this._percentageGoalToast.classList.remove("show", "showing");
      this._percentageGoalToast.style.display = "none";
    }

    if (this._deadlineGoalToast) {
      this._deadlineGoalToast.classList.remove("show", "showing");
      this._deadlineGoalToast.style.display = "none";
    }

    // Setup click tracking and handlers for credit goal toast
    if (this._creditGoalToast) {
      this.setupClickTracking(this._creditGoalToast, "Credit Goal");

      const closeButton = this._creditGoalToast.querySelector('button[data-dismiss="toast"]');
      if (closeButton) {
        closeButton.addEventListener("click", () => {
          this.dismissToast("reachyourgoal");
        });
      }
    }

    // Setup click tracking and handlers for percentage goal toast
    if (this._percentageGoalToast) {
      this.setupClickTracking(this._percentageGoalToast, "Percentage Goal");

      const closeButton = this._percentageGoalToast.querySelector('button[data-dismiss="toast"]');
      if (closeButton) {
        closeButton.addEventListener("click", () => {
          this.dismissToast("almostthere");
        });
      }
    }

    // Setup click tracking and handlers for deadline goal toast
    if (this._deadlineGoalToast) {
      this.setupClickTracking(this._deadlineGoalToast, "Deadline Goal");

      const closeButton = this._deadlineGoalToast.querySelector('button[data-dismiss="toast"]');
      if (closeButton) {
        closeButton.addEventListener("click", () => {
          this.dismissToast("deadline");
        });
      }
    }

    // Listen for global events to show toasters
    window.addEventListener(CMEGoalNotifications.ShowAlmostThereEvent, (ev: Event) => {
      const customEvent = ev as CustomEvent<{ percentage?: number }>;
      this.showToast("almostthere", customEvent.detail?.percentage);
    });

    window.addEventListener(CMEGoalNotifications.ShowReachYourGoalEvent, (ev: Event) => {
      const customEvent = ev as CustomEvent<{ credits?: number }>;
      this.showToast("reachyourgoal", undefined, customEvent.detail?.credits);
    });

    // Listen for global events to dismiss toasters
    window.addEventListener(CMEGoalNotifications.DismissAlmostThereEvent, () => {
      this.dismissToast("almostthere");
    });

    window.addEventListener(CMEGoalNotifications.DismissReachYourGoalEvent, () => {
      this.dismissToast("reachyourgoal");
    });

    // Listen for credit change events (triggered when user completes activity or uploads)
    // This allows "Almost there" reminder to fire immediately after credit changes
    document.addEventListener("creditchanged", () => {
      console.log("CMEGoalNotifications: Credit changed event detected, checking for 'Almost there' reminder");
      // Small delay to ensure UserProfile is updated with new credit values
      setTimeout(() => {
        this.checkAndTriggerToasts();
      }, 100);
    });

    // Setup trigger logic
    this.setupTriggers();

    // Expose debug API for console (e.g. CMEGoalToastDebug.showCreditGoal(3))
    const win = window as any;
    win.CMEGoalToastDebug = {
      showCreditGoal: (credits: number = 2) => {
        CMEGoalNotifications.getFirstInstance()?.showToastForDebug("reachyourgoal", undefined, credits);
        return `CME Goal Toast: Credit Goal shown (${credits} credits)`;
      },
      showAlmostThere: (percent: number = 75) => {
        CMEGoalNotifications.getFirstInstance()?.showToastForDebug("almostthere", percent);
        return `CME Goal Toast: Almost There shown (${percent}%)`;
      },
      showDeadline: (credits: number = 2) => {
        CMEGoalNotifications.getFirstInstance()?.showToastForDebug("deadline", undefined, credits);
        return `CME Goal Toast: Deadline shown (${credits} credits)`;
      },
      help: () => {
        console.log(
          "CMEGoalToastDebug: showCreditGoal(credits?) | showAlmostThere(percent?) | showDeadline(credits?)"
        );
        return "CMEGoalToastDebug usage logged above";
      }
    };
  }

  /**
   * Sets up the trigger logic for showing toasts based on CME goals
   */
  private setupTriggers(): void {
    // Check if UserProfile is available, otherwise wait for globalsloaded event
    if ((window as any).UserProfile) {
      console.log("CMEGoalNotifications: UserProfile available, checking toasts immediately");
      this.checkAndTriggerToasts();
    } else {
      console.log("CMEGoalNotifications: UserProfile not available, waiting for globalsloaded event");
      window.addEventListener("globalsloaded", () => {
        console.log("CMEGoalNotifications: globalsloaded event fired, checking toasts");
        this.checkAndTriggerToasts();
      }, { once: true });
    }
  }

  /**
   * Checks CME goals and triggers appropriate toasts based on business rules.
   *
   * BUSINESS RULES FOR CME GOAL REMINDERS (News Area Toasts):
   *
   * These reminders appear when a user is on Healio but outside of CME (e.g., in News).
   * There are 3 different reminder types, each with specific conditions:
   *
   * 1. OFF TRACK REMINDER:
   *    - Triggers: When user is exactly 30 days away OR exactly 7 days away from goal deadline
   *    - Condition: User MUST be off track (OnTrack === false) at that specific time
   *    - Frequency: Only appears ONE TIME at each checkpoint (30 days and 7 days)
   *    - Once shown: toastalerts.offtrack = true
   *    - Display: Uses the "Credit Goal" toast showing remaining credits needed
   *
   * 2. ALMOST THERE REMINDER:
   *    - Triggers: When user is 1 credit away from 75% or 100% complete (after activity complete/upload)
   *    - Condition: GoalCreditsRemaining matches expected value for 1-credit-from-milestone
   *    - Frequency: Only appears ONE TIME per milestone (75% and 100%)
   *    - Once shown: toastalerts.almostthere = true
   *    - Display: Uses the "Percentage Goal" toast showing 75% or 100%
   *    - Page restriction: ONLY on certificate page (docinfo.template = "Certificate")
   *
   * 3. DEADLINE IS HERE REMINDER:
   *    - Triggers: When the goal deadline has arrived (daysUntilDeadline === 0)
   *    - Condition: Goal is NOT complete (GoalCompletion < 100)
   *    - Frequency: Only appears ONE TIME
   *    - Once shown: toastalerts.deadline = true
   *    - Display: Uses the "Deadline Goal" toast showing remaining credits needed
   *
   * GENERAL RESTRICTIONS:
   * - Off Track and Deadline: only on /news pages or SpecialtyLandingPage (user "outside of CME")
   * - Almost There: only on certificate page (docinfo.template = "Certificate")
   * - All reminders are BLOCKED if:
   *   * Current date is before goal StartDate (goal hasn't started yet)
   *   * Current date is after goal EndDate (goal has expired)
   *   * GoalCreditsRemaining <= 0 (user has already met/exceeded goal)
   * - Cookie expiration: 7 days (prevents repeat reminders within a week)
   * - Cookie keys include goal EndDate to ensure uniqueness per goal period
   */
  private checkAndTriggerToasts(): void {
    try {
      this.checkAndTriggerToastsCore();
    } catch (err) {
      console.warn("CMEGoalNotifications: checkAndTriggerToasts failed (non-fatal)", err);
    }
  }

  private checkAndTriggerToastsCore(): void {
    console.log("CMEGoalNotifications: checkAndTriggerToasts called");
    const userProfile = (window as any).UserProfile as UserProfile;
    const docinfo = (window as any).Context?.docinfo;
    const path = (window as any).location?.pathname ?? "";

    if (!path) {
      return;
    }

    const pathLower = path.toLowerCase();
    // Off Track reminder: News or Specialty Landing (user "outside of CME")
    const isNewsOrSpecialtyPage =
      pathLower.indexOf("/news") === 0 || docinfo?.template === "SpecialtyLandingPage";
    // Deadline Is Here reminder: news pages only (same surface as first modal)
    const isNewsPage = pathLower.indexOf("/news") === 0;
    // Almost There reminder: only on certificate page (docinfo.template = "Certificate")
    const isCertificatePage = docinfo?.template === "Certificate";

    const isToastEligiblePage = isNewsOrSpecialtyPage || isCertificatePage;

    // If profile/cmegoals not ready yet (e.g. user just logged in), re-check once after a delay
    if (!userProfile || !userProfile.cmegoals) {
      console.log("CMEGoalNotifications: No userProfile or cmegoals", {
        hasUserProfile: !!userProfile,
        hasCmeGoals: !!userProfile?.cmegoals,
        path: pathLower
      });
      if (isToastEligiblePage && !this._scheduledPostLoginCheck) {
        this._scheduledPostLoginCheck = true;
        setTimeout(() => {
          this._scheduledPostLoginCheck = false;
          this.checkAndTriggerToasts();
        }, 2500);
      }
      return;
    }

    if (!docinfo) {
      return;
    }

    const cmegoals = userProfile.cmegoals;

    // On Certificate page, show the toast container (CSS hides it on all .cme pages by default)
    if (isCertificatePage) {
      document.body.classList.add("cme-certificate-page");
    }

    // Check if UserGoal exists
    if (!cmegoals.UserGoal) {
      return;
    }

    const userGoal = cmegoals.UserGoal;
    const now = new Date();
    const startDate = new Date(userGoal.StartDate);
    const endDate = new Date(userGoal.EndDate);

    // Calculate whole-day difference between today and the goal end date using UTC.
    // EndDate is stored as midnight UTC (e.g. Jan 29 00:00 UTC); using UTC ensures
    // "deadline day" is Jan 29 everywhere so the user sees the toast on Jan 29 local.
    const msPerDay = 24 * 60 * 60 * 1000;
    const startOfTodayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    const endDateDayUTC = Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate());
    const daysUntilDeadline = Math.floor((endDateDayUTC - startOfTodayUTC) / msPerDay);

    // BUSINESS RULE: Do not show toasts outside of the goal window (date-based, not timestamp)
    // Prevents reminders for expired goals or goals that haven't started yet.
    // Use calendar-day comparison so "deadline is here" can show on the end date day even after midnight.
    if (now < startDate) {
      return;
    }
    if (daysUntilDeadline < 0) {
      return; // Goal expired (past the end date day)
    }

    // Extract key values for reminder logic
    const isOffTrack = cmegoals.OnTrack === false;
    const goalCompletion = cmegoals.GoalCompletion || 0;
    const goalCreditsRemaining = cmegoals.GoalCreditsRemaining;

    // BUSINESS RULE: Don't show reminders if user has already met/exceeded their goal
    if (goalCreditsRemaining <= 0) {
      return;
    }

    // ========================================================================
    // REMINDER TYPE 1: OFF TRACK REMINDER (News / Specialty pages only)
    // ========================================================================
    if (isNewsOrSpecialtyPage) {
    // BUSINESS RULE: Show when user is 30 days OR 7 days away from deadline
    // AND user is off track at that specific time
    // Each checkpoint (30 days and 7 days) only appears ONCE

    // Debug logging for off-track reminder
    console.log("CMEGoalNotifications: Off-track check", {
      daysUntilDeadline,
      isOffTrack,
      OnTrack: cmegoals.OnTrack,
      goalCreditsRemaining,
      endDate: endDate.toISOString(),
      now: now.toISOString()
    });

    const offTrackCheckpoints = [30, 7];
    for (const checkpoint of offTrackCheckpoints) {
      if (daysUntilDeadline === checkpoint && isOffTrack) {
        const alerts = this.ensureToastAlerts(cmegoals);
        console.log("CMEGoalNotifications: Off-track condition met", {
          checkpoint,
          offtrackAlreadyShown: alerts.offtrack
        });
        if (!alerts.offtrack) {
          // Uses "Credit Goal" toast to show remaining credits needed
          console.log("CMEGoalNotifications: Showing off-track reminder");
          this.showToast("reachyourgoal", undefined, goalCreditsRemaining);
          this.markToastShown(cmegoals, "offtrack");
        } else {
          console.log("CMEGoalNotifications: Off-track reminder already shown (toastalerts.offtrack)");
        }
        return; // Exit early - only one reminder type should show at a time
      }
    }

    // Debug if conditions weren't met
    if (offTrackCheckpoints.includes(daysUntilDeadline)) {
      console.log("CMEGoalNotifications: At checkpoint but reminder not shown", {
        daysUntilDeadline,
        isOffTrack,
        reason: !isOffTrack ? "User is on track" : "Unknown"
      });
    }

    // ========================================================================
    // REMINDER TYPE 2: DEADLINE IS HERE REMINDER (News pages only)
    // ========================================================================
    // BUSINESS RULE: When goal deadline has arrived and user has not completed their goal.
    // Show only on news pages (like the first modal). Only appears ONE TIME (toastalerts.deadline).
    console.log("CMEGoalNotifications: Deadline check", {
      isNewsPage,
      path: pathLower,
      daysUntilDeadline,
      goalCompletion,
      endDateDayUTC: new Date(endDateDayUTC).toISOString(),
      startOfTodayUTC: new Date(startOfTodayUTC).toISOString()
    });
    if (isNewsPage && daysUntilDeadline === 0 && goalCompletion < 100) {
      const alerts = this.ensureToastAlerts(cmegoals);
      if (!alerts.deadline) {
        console.log("CMEGoalNotifications: Showing 'Deadline is here' reminder");
        this.showToast("deadline", undefined, goalCreditsRemaining);
        this.markToastShown(cmegoals, "deadline");
      } else {
        console.log("CMEGoalNotifications: Deadline reminder already shown (toastalerts.deadline)");
      }
      return; // Exit early - deadline reminder takes priority
    }

    } // end isNewsOrSpecialtyPage

    // ========================================================================
    // REMINDER TYPE 3: ALMOST THERE REMINDER (Certificate page only)
    // ========================================================================
    if (!isCertificatePage) {
      return;
    }
    // BUSINESS RULE: Show when user is 1 credit away from being 75% or 100% complete
    // Triggered by completed Healio activity or uploaded activity that brings them
    // to exactly 1 credit away from 75% or 100% milestone
    // Only appears ONE TIME per milestone (1 credit from 75% and 1 credit from 100%)

    const goalCredits = parseFloat(userGoal.Credits);
    if (goalCredits === 0) {
      return;
    }

    // Calculate what GoalCreditsRemaining should be if user is 1 credit away from milestones
    // For 75%: If user needs 1 credit to reach 75%, they've earned (75% - 1 credit)
    //          So GoalCreditsRemaining = goalCredits - (goalCredits * 0.75 - 1) = goalCredits * 0.25 + 1
    // For 100%: If user needs 1 credit to reach 100%, they've earned (100% - 1 credit)
    //           So GoalCreditsRemaining = goalCredits - (goalCredits - 1) = 1

    const expectedRemainingFor75 = (goalCredits * 0.25) + 1.0;
    const expectedRemainingFor100 = 1.0;

    // Check if current GoalCreditsRemaining matches expected value (within tolerance for floating point)
    const tolerance = 0.01;
    const isOneCreditFrom75 = Math.abs(goalCreditsRemaining - expectedRemainingFor75) < tolerance;
    const isOneCreditFrom100 = Math.abs(goalCreditsRemaining - expectedRemainingFor100) < tolerance;

    console.log("CMEGoalNotifications: Almost there check", {
      goalCredits,
      goalCreditsEarned: cmegoals.GoalCreditsEarned,
      goalCreditsRemaining: goalCreditsRemaining,
      expectedRemainingFor75,
      expectedRemainingFor100,
      isOneCreditFrom75,
      isOneCreditFrom100
    });

    // Check 100% milestone first (higher priority - if both conditions met, show 100%)
    if (isOneCreditFrom100) {
      const alerts = this.ensureToastAlerts(cmegoals);
      if (!alerts.almostthere) {
        console.log("CMEGoalNotifications: Showing 'Almost there' reminder for 100% milestone");
        // Uses "Percentage Goal" toast to show 100% completion
        this.showToast("almostthere", 100);
        this.markToastShown(cmegoals, "almostthere");
      } else {
        console.log("CMEGoalNotifications: 100% milestone reminder already shown (toastalerts.almostthere)");
      }
      return; // Exit early - only one reminder type should show at a time
    }

    // Check 75% milestone
    if (isOneCreditFrom75) {
      const alerts = this.ensureToastAlerts(cmegoals);
      if (!alerts.almostthere) {
        console.log("CMEGoalNotifications: Showing 'Almost there' reminder for 75% milestone");
        // Uses "Percentage Goal" toast to show 75% completion
        this.showToast("almostthere", 75);
        this.markToastShown(cmegoals, "almostthere");
      } else {
        console.log("CMEGoalNotifications: 75% milestone reminder already shown (toastalerts.almostthere)");
      }
      return; // Exit early - only one reminder type should show at a time
    }
  }


  /**
   * Returns the single toastalerts object. Merges both if backend sent ToastAlerts and we had toastalerts
   * (union of "true" flags so no state is lost), then keeps only toastalerts and removes ToastAlerts.
   */
  private ensureToastAlerts(cmegoals: CMEGoals): ToastAlerts {
    const fromBackend = cmegoals.ToastAlerts;
    const fromClient = cmegoals.toastalerts;

    let merged: ToastAlerts;
    if (fromBackend && fromClient) {
      merged = {
        offtrack: fromBackend.offtrack || fromClient.offtrack,
        almostthere: fromBackend.almostthere || fromClient.almostthere,
        deadline: fromBackend.deadline || fromClient.deadline
      };
    } else if (fromClient) {
      merged = fromClient;
    } else if (fromBackend) {
      merged = fromBackend;
    } else {
      merged = { ...CMEGoalNotifications._defaultToastAlerts };
    }

    cmegoals.toastalerts = merged;
    delete (cmegoals as any).ToastAlerts;
    return cmegoals.toastalerts;
  }

  /**
   * Marks a toast as shown on cmegoals.toastalerts and persists to MongoDB via API (no cookies/localStorage).
   */
  private markToastShown(cmegoals: CMEGoals, type: "offtrack" | "almostthere" | "deadline"): void {
    const alerts = this.ensureToastAlerts(cmegoals);
    if (type === "offtrack") alerts.offtrack = true;
    else if (type === "almostthere") alerts.almostthere = true;
    else if (type === "deadline") alerts.deadline = true;

    this.persistToastAlertsToServer(alerts);
  }

  /**
   * Persists toastalerts to MongoDB via edu/api/updatetoastalerts so "already shown" survives refresh.
   */
  private persistToastAlertsToServer(alerts: ToastAlerts): void {
    const url = "/edu/api/updatetoastalerts";
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        offtrack: alerts.offtrack,
        almostthere: alerts.almostthere,
        deadline: alerts.deadline
      }),
      credentials: "same-origin"
    }).catch(() => {
      /* non-fatal; in-memory state still updated */
    });
  }

  /**
   * Public helper for debug/manual launch of toasts from console.
   * Example: CMEGoalNotifications.getFirstInstance()?.showToastForDebug("deadline", undefined, 2)
   */
  public showToastForDebug(
    type: "almostthere" | "reachyourgoal" | "deadline",
    percentage?: number,
    credits?: number
  ): void {
    this.showToast(type, percentage, credits);
  }

  /**
   * Shows a toast notification by updating and displaying the existing toast element
   */
  private showToast(
    type: "almostthere" | "reachyourgoal" | "deadline",
    percentage?: number,
    credits?: number
  ): void {
    let toastElement: HTMLElement | null = null;

    if (type === "almostthere") {
      toastElement = this._percentageGoalToast;
    } else if (type === "reachyourgoal") {
      toastElement = this._creditGoalToast;
    } else {
      toastElement = this._deadlineGoalToast;
    }

    if (!toastElement) {
      console.warn(`CMEGoalNotifications: Toast element for ${type} not found`);
      return;
    }

    // Update dynamic content by replacing placeholders
    if (type === "almostthere" && percentage !== undefined) {
      // Replace {percent} placeholder with actual percentage
      const percentElement = toastElement.querySelector(".toast-credits");
      if (percentElement) {
        percentElement.textContent = `${percentage}%`;
      }
    } else if ((type === "reachyourgoal" || type === "deadline") && credits !== undefined) {
      // Replace {credits} placeholder with actual credits (credit goal and deadline toasts)
      const creditsElement = toastElement.querySelector(".toast-credits");
      if (creditsElement) {
        creditsElement.textContent = `${credits} Credits`;
      }
    }

    // Track toast open event
    const toastLabel =
      type === "almostthere"
        ? "Percentage Goal"
        : type === "reachyourgoal"
          ? "Credit Goal"
          : "Deadline Goal";
    moduletracking.UseGoogleTrack("CME Goal Toast", "Open", toastLabel);

    // Show the toast with animation
    requestAnimationFrame(() => {
      toastElement.style.display = "";
      toastElement.classList.remove("hide");
      toastElement.classList.add("show", "showing");

      // Focus management for accessibility
      const focusableElement = toastElement.querySelector<HTMLElement>("[tabindex='0']");
      if (focusableElement) {
        focusableElement.focus();
      }
    });
  }

  /**
   * Sets up click tracking for buttons and links in a toast.
   *
   * GA events (Category: "CME Goal Toast"):
   * - Open / Credit Goal – when Credit Goal toast is displayed (off-track reminder)
   * - Open / Percentage Goal – when Percentage Goal toast is displayed (almost-there milestone)
   * - Open / Deadline Goal – when Deadline Goal toast is displayed (deadline is here)
   * - Click / Credit Goal_Close – user clicked close (X) on Credit Goal toast
   * - Click / Credit Goal_View Recommended CME – user clicked "View Recommended CME" on Credit Goal toast
   * - Click / Percentage Goal_Close – user clicked close (X) on Percentage Goal toast
   * - Click / Percentage Goal_View Recommended CME – user clicked "View Recommended CME" on Percentage Goal toast
   * - Click / Percentage Goal_Edit Goal – user clicked "Edit Goal" on Percentage Goal toast
   * - Click / Deadline Goal_Close – user clicked close (X) on Deadline Goal toast
   * - Click / Deadline Goal_View Recommended CME – user clicked "View Recommended CME" on Deadline Goal toast
   * - Click / Deadline Goal_Edit Goal – user clicked "Edit Goal" on Deadline Goal toast
   */
  private setupClickTracking(toastElement: HTMLElement, toastType: string): void {
    // Track close button clicks
    const closeButton = toastElement.querySelector('button[data-dismiss="toast"]');
    if (closeButton) {
      closeButton.addEventListener("click", () => {
        moduletracking.UseGoogleTrack("CME Goal Toast", "Click", `${toastType}_Close`);
      });
    }

    // Track "View Recommended CME" link clicks
    const viewRecommendedLink = toastElement.querySelector('a.toast-action-btn');
    if (viewRecommendedLink) {
      viewRecommendedLink.addEventListener("click", () => {
        moduletracking.UseGoogleTrack("CME Goal Toast", "Click", `${toastType}_View Recommended CME`);
      });
    }

    // Track "Edit Goal" link clicks (percentage and deadline toasts)
    const editGoalLink = toastElement.querySelector('a.toast-edit-goal-btn');
    if (editGoalLink) {
      editGoalLink.addEventListener("click", () => {
        moduletracking.UseGoogleTrack("CME Goal Toast", "Click", `${toastType}_Edit Goal`);
      });
    }
  }

  /**
   * Dismisses a toast by type
   */
  private dismissToast(type: "almostthere" | "reachyourgoal" | "deadline"): void {
    let toastElement: HTMLElement | null = null;

    if (type === "almostthere") {
      toastElement = this._percentageGoalToast;
    } else if (type === "reachyourgoal") {
      toastElement = this._creditGoalToast;
    } else {
      toastElement = this._deadlineGoalToast;
    }

    if (!toastElement) {
      return;
    }

    // Hide the toast with animation
    toastElement.classList.remove("show", "showing");
    toastElement.classList.add("hide");

    // Hide after animation completes
    setTimeout(() => {
      toastElement.style.display = "none";
      toastElement.classList.remove("hide");
    }, 500); // Match CSS animation duration
  }

  /**
   * Static method to trigger the "almost there" toaster
   * @param percentage Optional percentage value to display
   */
  public static showAlmostThere(percentage?: number): void {
    window.dispatchEvent(
      EventUtil.createCustomEvent(
        CMEGoalNotifications.ShowAlmostThereEvent,
        { percentage }
      )
    );
  }

  /**
   * Static method to trigger the "reach your goal" toaster
   * @param credits Optional credits value to display
   */
  public static showReachYourGoal(credits?: number): void {
    window.dispatchEvent(
      EventUtil.createCustomEvent(
        CMEGoalNotifications.ShowReachYourGoalEvent,
        { credits }
      )
    );
  }

  /**
   * Static method to dismiss all "almost there" toasters
   */
  public static dismissAlmostThere(): void {
    window.dispatchEvent(
      EventUtil.createCustomEvent(CMEGoalNotifications.DismissAlmostThereEvent, null)
    );
  }

  /**
   * Static method to dismiss all "reach your goal" toasters
   */
  public static dismissReachYourGoal(): void {
    window.dispatchEvent(
      EventUtil.createCustomEvent(CMEGoalNotifications.DismissReachYourGoalEvent, null)
    );
  }
}

ComponentFactory.registerComponent("CMEGoalNotifications", CMEGoalNotifications);
